import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { useStore, API } from "@/context/StoreContext";

export default function FaqWidget() {
  const { faqOpen, setFaqOpen, t, lang } = useStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sidRef = useRef(null);

  if (!sidRef.current) {
    sidRef.current = sessionStorage.getItem("pamak-faq-sid") || ("web-" + Math.random().toString(36).slice(2) + Date.now().toString(36));
    sessionStorage.setItem("pamak-faq-sid", sidRef.current);
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, faqOpen]);

  const send = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/faq/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question, session_id: sidRef.current + "-" + lang }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n");
        buf = parts.pop();
        for (const line of parts) {
          if (line.startsWith("data: ")) {
            const ev = JSON.parse(line.slice(6));
            if (ev.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + ev.delta };
                return copy;
              });
            }
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "…" };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <button
        className="faq-fab"
        data-testid="faq-toggle-button"
        aria-label={t("faq.title")}
        onClick={() => setFaqOpen(!faqOpen)}
      >
        {faqOpen ? <X size={20} /> : <MessageCircle size={20} />}
      </button>

      <div className={`faq-panel ${faqOpen ? "open" : ""}`} data-testid="faq-panel" aria-hidden={!faqOpen}>
        <div className="faq-head">
          <p className="section-title-small" style={{ marginBottom: 0 }}>PAMAK</p>
          <strong>{t("faq.title")}</strong>
        </div>

        <div className="faq-messages" data-lenis-prevent data-testid="faq-messages" ref={scrollRef}>
          <div className="faq-msg assistant">{t("faq.hello")}</div>
          {messages.map((m, i) => (
            <div key={i} className={`faq-msg ${m.role}`} data-testid={`faq-msg-${m.role}`}>
              {m.text || "…"}
            </div>
          ))}
        </div>

        <form className="faq-input" onSubmit={send} data-testid="faq-form">
          <input
            data-testid="faq-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("faq.placeholder")}
            aria-label={t("faq.title")}
          />
          <button type="submit" data-testid="faq-send-button" disabled={streaming} aria-label="Send">
            <Send size={16} />
          </button>
        </form>
      </div>
    </>
  );
}
